/** Shape shared by localStorage autosave and estimation_data.ui_draft.
 *
 * Contact fields (persisted on create-quote drafts):
 * - selectedLead — Owner (property owner) snapshot
 * - selectedContractor — Contractor snapshot when useAsContractor is false
 * - useAsContractor — checkbox; when true contractor mirrors owner
 * - clientNameSource — Client Estimate "Client:" name: owner | contractor
 */

export const UI_DRAFT_KEYS = [
    'clientInfo',
    'clientInfoMode',
    'selectedLead',
    'selectedContractor',
    'useAsContractor',
    'clientNameSource',
    'selectedProjectId',
    'selectedProjectNo',
    'workType',
    'roofType',
    'stories',
    'steepRoofs',
    'flatRoofs',
    'activeSteepIdx',
    'activeFlatIdx',
    'flatUseScaraface',
    'flatUseTelehandler',
    'flatUseBoomLift',
    'flatUseBathroom',
    'inputs',
    'commissionPct',
    'result',
    'lineOverrides',
    'unitCostOverrides',
    'extraLines',
    'disabledItemIds',
    'attachments',
    'relatedQuoteFiles',
    'viewFiles',
    'architectProject',
    'scopeHtml',
    'woodHtml',
    'permitsHtml',
    'permitsHidden',
    'completionDays',
] as const;

export type UiDraft = Record<(typeof UI_DRAFT_KEYS)[number], unknown>;

function slimFileList(value: unknown): unknown {
    if (!Array.isArray(value)) return value;
    return value.map(item => {
        if (!item || typeof item !== 'object') return item;
        const row = item as Record<string, unknown>;
        if (typeof row.project_file_id === 'number') {
            return {
                project_file_id: row.project_file_id,
                name: row.name,
                filename: row.filename,
                content_type: row.content_type,
            };
        }
        return {
            id: row.id,
            name: row.name,
            filename: row.filename,
            content_type: row.content_type,
            ...(typeof row.uploaded_at === 'string' ? { uploaded_at: row.uploaded_at } : {}),
        };
    });
}

export function buildUiDraft(state: UiDraft): UiDraft {
    const out = {} as UiDraft;
    for (const key of UI_DRAFT_KEYS) {
        if (key === 'attachments' || key === 'relatedQuoteFiles' || key === 'viewFiles') {
            out[key] = slimFileList(state[key]);
        } else if (key === 'selectedLead' || key === 'selectedContractor') {
            const person = state[key];
            if (!person || typeof person !== 'object') {
                out[key] = null;
            } else {
                const p = person as Record<string, unknown>;
                out[key] = {
                    id: Number(p.id) || 0,
                    name: String(p.name ?? ''),
                    company_name: String(p.company_name ?? ''),
                    email: String(p.email ?? ''),
                    phone_number: String(p.phone_number ?? ''),
                };
            }
        } else if (key === 'useAsContractor') {
            out[key] = state.useAsContractor !== false;
        } else {
            out[key] = state[key];
        }
    }
    if (out.useAsContractor) {
        out.selectedContractor = null;
    }
    return out;
}

/** Fails if localStorage payload and ui_draft diverge — runnable check for save restore. */
export function assertUiDraftMatchesLocalStorageKeys(draft: Record<string, unknown>): void {
    for (const key of UI_DRAFT_KEYS) {
        if (!(key in draft)) {
            throw new Error(`ui_draft missing key: ${key}`);
        }
    }
}

/** Best-effort camelCase draft from pre-ui_draft estimation_data snapshots. */
export function draftFromLegacyEstimationData(
    data: Record<string, unknown>,
): Record<string, unknown> {
    const client = (data.client_information ?? {}) as Record<string, unknown>;
    const ctx = (data.project_context ?? {}) as Record<string, unknown>;
    const roof = (data.roof_system ?? {}) as Record<string, unknown>;
    const meas = (data.measurements ?? {}) as Record<string, unknown>;
    const cost = (data.cost_sheet ?? {}) as Record<string, unknown>;
    const breakdown = (cost.cost_breakdown ?? {}) as Record<string, unknown>;
    const summary = (cost.internal_summary ?? {}) as Record<string, unknown>;

    const lineOverrides: Record<string, number> = {};
    const unitCostOverrides: Record<string, number> = {};
    for (const section of [breakdown.steep, breakdown.flat]) {
        if (!Array.isArray(section)) continue;
        for (const row of section) {
            const r = row as Record<string, unknown>;
            if (typeof r.item_id === 'string' && typeof r.qty === 'number') {
                lineOverrides[r.item_id] = r.qty;
            }
            if (typeof r.item_id === 'string' && typeof r.unit_cost === 'number') {
                unitCostOverrides[r.item_id] = r.unit_cost;
            }
        }
    }

    const steepExtras = Array.isArray(breakdown.steep_additional_items)
        ? breakdown.steep_additional_items
        : [];
    const flatExtras = Array.isArray(breakdown.flat_additional_items)
        ? breakdown.flat_additional_items
        : [];
    const otherExtras = Array.isArray(cost.additional_items) ? cost.additional_items : [];

    const propertyOwner = client.property_owner;
    const contractorSnap = client.contractor;
    const useAsContractor =
        client.use_as_contractor !== false &&
        (client.contractor_id == null ||
            client.property_owner_id == null ||
            Number(client.contractor_id) === Number(client.property_owner_id));

    const selectedLead =
        propertyOwner && typeof propertyOwner === 'object'
            ? {
                  id: Number((propertyOwner as { id?: number }).id) || 0,
                  name: String((propertyOwner as { name?: string }).name ?? client.client_name ?? ''),
                  company_name: String(
                      (propertyOwner as { company_name?: string }).company_name ?? '',
                  ),
                  email: String(
                      (propertyOwner as { email?: string }).email ?? client.email ?? '',
                  ),
                  phone_number: String(
                      (propertyOwner as { phone_number?: string }).phone_number ??
                          client.phone ??
                          '',
                  ),
              }
            : String(client.client_name ?? '').trim()
              ? {
                    id: Number(client.property_owner_id) || 0,
                    name: String(client.client_name ?? ''),
                    company_name: '',
                    email: String(client.email ?? ''),
                    phone_number: String(client.phone ?? ''),
                }
              : null;

    const selectedContractor =
        !useAsContractor && contractorSnap && typeof contractorSnap === 'object'
            ? {
                  id: Number((contractorSnap as { id?: number }).id) || 0,
                  name: String((contractorSnap as { name?: string }).name ?? ''),
                  company_name: String(
                      (contractorSnap as { company_name?: string }).company_name ?? '',
                  ),
                  email: String((contractorSnap as { email?: string }).email ?? ''),
                  phone_number: String(
                      (contractorSnap as { phone_number?: string }).phone_number ?? '',
                  ),
              }
            : null;

    return {
        clientInfo: {
            clientName: String(client.client_name ?? ''),
            address: String(client.address ?? ''),
            city: String(client.city ?? ''),
            state: String(client.state ?? 'FL'),
            zipCode: String(client.zip_code ?? ''),
            email: String(client.email ?? ''),
            phone: String(client.phone ?? ''),
            folio: String(client.folio ?? ''),
            yearBuilt: String(client.year_built ?? ''),
            propertyType: String(client.property_type ?? 'Residential'),
        },
        clientInfoMode: (client.client_info_mode as string) === 'create' ? 'create' : 'search',
        selectedLead,
        selectedContractor,
        useAsContractor,
        clientNameSource:
            client.client_name_source === 'contractor' ? 'contractor' : 'owner',
        selectedProjectId: (client.project_id as number | null) ?? null,
        selectedProjectNo: (client.project_no as number | null) ?? null,
        workType: ctx.work_type ?? null,
        roofType: ctx.roof_type ?? null,
        stories: typeof ctx.stories === 'number' ? ctx.stories : 1,
        steepSystemId: roof.steep_system_id ?? 'SHINGLE',
        flatSystemId: roof.flat_system_id ?? 'NONE-FLAT',
        flatSubstrate: roof.flat_substrate ?? 'WOOD',
        flatScuppers: roof.flat_scuppers ?? 0,
        flatDrains: roof.flat_drains ?? 0,
        shingleUnderlaymentId: roof.shingle_underlayment_id,
        tileUnderlaymentId: roof.tile_underlayment_id,
        tileTypeId: roof.tile_type_id,
        tileFlashingMetal: roof.tile_flashing_metal,
        tileFlashingSize: roof.tile_flashing_size,
        metalUnderlaymentId: roof.metal_underlayment_id,
        metalTypeId: roof.metal_type_id,
        builtupInsulationId: roof.builtup_insulation_id,
        builtupR20Qty: roof.builtup_r20_qty,
        builtupSmoothQty: roof.builtup_smooth_qty,
        builtupSm15Qty: roof.builtup_sm15_qty,
        builtupCapId: roof.builtup_cap_id,
        builtupEdgeMetal: roof.builtup_edge_metal,
        builtupEdgeSize: roof.builtup_edge_size,
        builtupParapetMetal: roof.builtup_parapet_metal,
        builtupParapetCoping: roof.builtup_parapet_coping,
        polyglassIpQty: roof.polyglass_ip_qty,
        polyglassEdgeMetal: roof.polyglass_edge_metal,
        polyglassEdgeSize: roof.polyglass_edge_size,
        wpSbWarranty: roof.wp_sb_warranty,
        flatUseScaraface: Boolean(roof.flat_use_scaraface),
        flatUseTelehandler: Boolean(roof.flat_use_telehandler),
        flatUseBoomLift: Boolean(roof.flat_use_boom_lift),
        flatUseBathroom: Boolean(roof.flat_use_bathroom),
        inputs: {
            steep_area: meas.steep_area ?? 0,
            steep_edge: meas.steep_edge ?? 0,
            ridge: meas.ridge ?? 0,
            valley: meas.valley ?? 0,
            steep_parapet: meas.steep_parapet ?? 0,
            flat_area: meas.flat_area ?? 0,
            flat_edge: meas.flat_edge ?? 0,
            flat_parapet: meas.flat_parapet ?? 0,
            wood_plywood_sheets: meas.wood_plywood_sheets ?? 5,
            wood_lumber_pieces: meas.wood_lumber_pieces ?? 5,
            plumbing_projections: meas.plumbing_projections ?? 0,
            ventilation_projections: meas.ventilation_projections ?? 0,
            overhead_pct: meas.overhead_pct ?? 0.1,
            profit_pct: meas.profit_pct ?? 0.5,
        },
        commissionPct: typeof summary.commission_pct === 'number' ? summary.commission_pct : 0.05,
        result: cost.calculation ?? null,
        lineOverrides,
        unitCostOverrides,
        extraLines: [...steepExtras, ...flatExtras, ...otherExtras],
        disabledItemIds: [],
        attachments: Array.isArray(data.attachments) ? data.attachments : [],
        relatedQuoteFiles: Array.isArray(data.related_quote_files) ? data.related_quote_files : [],
        viewFiles: Array.isArray(data.view_files) ? data.view_files : [],
        activeSteepIdx: 0,
        activeFlatIdx: 0,
    };
}
